<template>
    <b-row id="list" class="no-gutters">
        <b-col cols="12" class="mt-2 mb-2">
            <p class="table-section-title">
                {{ $t('dashboard.dams') }}
            </p>
        </b-col>

        <b-col cols="12" class="for-info-wrapper d-flex flex-wrap">
            {{ /* left panel with 2 dropdowns */ }}
            <b-col cols="12" lg="7">
                <b-row>
                    <b-col cols="12" xl="6" class="mb-4 mb-sm-0">
                        <b-row>
                            <b-col cols="3" class="d-flex align-items-center justify-content-end pl-sm-0">
                                <div class="dropdown-label-grey mr-1">{{ $t('dashboard.table.sort-by') }}:</div>
                            </b-col>
                            <b-col class="px-0">
                                <b-dropdown
                                    id="dropdown-1"
                                    variant="link"
                                    class="m-md-2 br-border"
                                    toggle-class="text-decoration-none outline-none"
                                    no-caret
                                    block
                                >
                                    <template v-slot:button-content>
                                        <div class="d-flex justify-content-between" style="width: 98%;">
                                        <span class="text-body">
                                            {{ sortBy.label || $t('dashboard.table.pick') }}
                                        </span>
                                            <span>
                                            <SvgIcon name="chevron-down"/>
                                        </span>
                                        </div>
                                    </template>

                                    <b-dropdown-item
                                        @click="sortBy = ''"
                                    >
                                    <span class="font-italic">
                                        {{ $t('dashboard.table.show-all') }}
                                    </span>
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        v-for="(field, index) in fields"
                                        :key="`sort-by-${index}`"
                                        @click="sortBy = field"
                                    >{{ field.label }}
                                    </b-dropdown-item>
                                </b-dropdown>
                            </b-col>
                        </b-row>
                    </b-col>

                    <b-col cols="12" xl="6" class="mb-4 mb-sm-0">
                        <b-row>
                            <b-col cols="3" class="d-flex align-items-center justify-content-end pl-sm-0">
                                <div class="dropdown-label-grey mr-1">{{ $t('dashboard.table.show-status') }}:</div>
                            </b-col>
                            <b-col class="px-0">
                                <b-dropdown
                                    id="dropdown-2"
                                    variant="link"
                                    class="m-md-2 br-border"
                                    toggle-class="text-decoration-none outline-none"
                                    no-caret
                                    block
                                >
                                    <template v-slot:button-content>
                                        <div class="d-flex justify-content-between" style="width: 98%;">
                                        <span class="text-body">
                                            {{ localizeFilterByLabel(filterBy) }}
                                        </span>
                                            <span>
                                            <SvgIcon name="chevron-down"/>
                                        </span>
                                        </div>
                                    </template>

                                    <b-dropdown-item
                                        @click="filterBy = ''"
                                    >
                                    <span class="font-italic">
                                        {{ $t('dashboard.table.show-all') }}
                                    </span>
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        v-for="(status, index) in statuses"
                                        :key="`stat-k-${index}`"
                                        @click="filterBy = status[0]"
                                    >{{ status[1] }}
                                    </b-dropdown-item>
                                </b-dropdown>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </b-col>

            {{ /* right panel with pagination */ }}
            <b-col cols="12" lg="5" class="d-flex justify-content-end align-items-center">
                <!-- column picker  -->
                <b-dropdown
                    variant="link"
                    toggle-class="text-decoration-none outline-none"
                    no-caret class="mx-0 my-0 outline-none"
                >
                    <template v-slot:button-content>
                        <WhiteIconButton icon="list"></WhiteIconButton>
                    </template>

                    <b-dropdown-header id="dropdown-header-label">
                        {{ $t('dashboard.table.columns-in-table') }}
                    </b-dropdown-header>

                    {{ /* SEE dev notes bellow on @click.native.capture.stop */ }}
                    <b-dropdown-item
                        event="hover"
                        v-for="(field, index) in fields"
                        :key="`field-${index}`"
                        @click.native.capture.stop=""
                    >
                        <b-form-checkbox v-model="field.visible">{{ field.label }}</b-form-checkbox>
                    </b-dropdown-item>
                </b-dropdown>

                <b-pagination
                    v-model="currentPage"
                    :total-rows="lenVisibleRows"
                    :per-page="perPage"
                    aria-controls="my-table"
                    :hide-goto-end-buttons="true"
                    :limit="3"
                    class="mb-0 normal-z-index"
                ></b-pagination>
            </b-col>
        </b-col>

        <b-col cols="12">
            <!--  table -->
            <b-table hover small
                :per-page="perPage"
                :current-page="currentPage"
                :fields="visibleFields"
                :items="visibleItems"
                :sort-icon-left="true"
                :sort-by="sortBy.key || ''"
                @row-clicked="(item) => rowClickHandler(item)"
                class="table-responsive-sm"
            >
                <!-- A custom formatted data column cell for status-->
                <template v-slot:cell(status)="data">
                    <StatusDotText :status-arr="data.value"></StatusDotText>
                </template>
            </b-table>
        </b-col>
    </b-row>

</template>

<script>
    // assets
    import {levelMeters} from "../../constants/sensors-constants";
    // utils, vuex, localization
    import {convertNumStateToName, translateStatus} from "../../utils/common";
    import {SET_CURRENT_DAM} from "../../store/actions/dashboard";
    // components
    import StatusDotText from "../shared/StatusDotText";
    import WhiteIconButton from "../shared/WhiteIconButton";
    import SvgIcon from "../shared/SvgIcon";

    /**
     * Table, showing all the stations with their status.
     * Has pagination and pagination selector.
     * NOTE: Should have filters and dynamic fields.
     */

    /**
     * DEV notes:
     *   <!-- the click event's propagation will be stopped -->
     *   <a v-on:click.stop="doThis"></a>
     *   <!-- the submit event will no longer reload the page -->
     *   <form v-on:submit.prevent="onSubmit"></form>
     *   <!-- use capture mode when adding the event listener -->
     *   <!-- i.e. an event targeting an inner element is handled here before being handled by that element -->
     *   <div v-on:click.capture="doThis">...</div>
     */

    export default {
        name: "TableOverview",
        components: {
            StatusDotText,
            WhiteIconButton,
            SvgIcon
        },
        props: {
            data: Object,
        },
        data() {
            return {
                levelMeters,
                perPage: 10,
                currentPage: 1,
                sortBy: '',
                filterBy: '',
                shownColumns: [],
                fields: [
                    {
                        key: 'dam',
                        label: this.$i18n.t('dashboard.table.objects'),
                        sortable: true,
                        stickyColumn: true,
                        visible: true
                    },
                    {
                        key: 'level',
                        label: this.$i18n.t('dashboard.table.level'),
                        sortable: true,
                        visible: true
                    },
                                        {
                        key: 'coordinates',
                        label: this.$i18n.t('dashboard.table.coordinates'),
                        sortable: true,
                        visible: true
                    },
                                        {
                        key: 'status',
                        label: this.$i18n.t('dashboard.table.status'),
                        sortable: true,
                        visible: true
                    }
                ]
            }
        },
        computed: {
            /**
             * This converts the PS_stations
             * into suitable format for the b-table to use.
             * @returns {[]}
             */
            items() {
                let formattedItems = [];

                if (!this.data) {
                    return formattedItems;
                }

                // for all stations, create an item with structured data (for table)
                for (let stationArr of this.data.PS_stations) {
                    let item = {};

                    // name (objects)
                    item['dam'] = this.$i18n.locale && this.$i18n.locale !== 'en' ? stationArr[6] : stationArr[0];

                    // get sensor keys, iterate through the known levelmeters, use the name if found as key for value
                    const sensorKeys = Object.keys(stationArr[5]);
                    for (let name of levelMeters) {
                        if (sensorKeys.includes(name)) {
                            item['level'] = stationArr[5][name];
                            break;
                        }
                    }

                    // coordinates
                    item['coordinates'] = `${stationArr[2]}\u00B0 ${this.$i18n.t('dashboard.north')}, `;
                    item['coordinates'] += `${stationArr[3]}\u00B0 ${this.$i18n.t('dashboard.east')}`;

                    // status
                    const statusAsDigit = this.data.ps_state_dict[`${stationArr[0]}_${stationArr[1]}`];
                    item['status'] = translateStatus(statusAsDigit);

                    // (internal) original object, used on click to set global selected dam
                    item['_dam'] = stationArr;

                    formattedItems.push(item);
                }
                return formattedItems;
            },
            /**
             *  To the ready items, apply a filter for which
             *  of them to show, based on (FOR NOW) the status codes.
             */
            visibleItems() {
                if (this.filterBy === '') {
                    return this.items;
                }
                return this.items.filter(item => item.status[0] === this.filterBy);
            },
            statuses() {
                  let statuses = [];
                  let _statusNames = [];
                  for (let item of this.items) {
                      if (!_statusNames.includes(item.status[0])) {
                          _statusNames.push(item.status[0]);
                          statuses.push(item.status);
                      }
                  }
                  return statuses;
            },
            /**
             * Computes how many rows the table has.
             * Used by pagination.
             * NOTE: we show pagination for displayed items, not all.
             * @returns {number}
             */
            lenVisibleRows() {
                return this.visibleItems.length;
            },
            /**
             * Only the selected columns will be shown.
             */
            visibleFields() {
                return this.fields.filter(field => field.visible);
            }
        },
        methods: {
            /**
             * Used to select a station and open
             * the right side panel.
             * _dam is the original object itself.
             * @param item
             */
            rowClickHandler(item) {
                this.$store.dispatch(SET_CURRENT_DAM, item['_dam']);
            },
          /**
           * Get the status code we are filtering by
           * and convert to text + localize
           * @param {number} filterByCode
           * @returns {string}
           */
            localizeFilterByLabel(filterByCode) {
              // set default
              let filterByNiceLabel = this.$i18n.t('dashboard.table.pick');
              // get new label
              const newLabelStr = convertNumStateToName(filterByCode);

              if (newLabelStr) {
                  filterByNiceLabel = newLabelStr;
              }

              return filterByNiceLabel;
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "src/assets/scss/variables";

    #list {
        padding: 1.8rem;
    }

    .b-dropdown::v-deep {
        .dropdown-menu {
            overflow: auto;
        }
    }

    .normal-z-index {
        z-index: 1;
    }

</style>